import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { AuthGuard } from './components/AuthGuard/AuthGuard';
import { UserRoleGuard } from './components/AuthGuard/UserRoleGuard';
import { useAnalytics } from './hooks/useAnalytics';
import CookieConsent from './components/CookieConsent/CookieConsent';
import HomePage from './pages/LandingPage/LandingPage'; // Keep home page for initial load
import NotFound from './components/NotFound/NotFound';
import GlobalErrorBoundary from './components/ErrorBoundary/GlobalErrorBoundary';
import BottomNavigation from './components/BottomNavigation/BottomNavigation';
import { libraryCacheManager } from './utils/libraryCache';
import { useLibraryCache } from './hooks/useLibraryCache';
import { isMobile } from 'react-device-detect';
import { createPortal } from 'react-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { GlobalFreeIcons } from '@hugeicons/core-free-icons';

/**
 * Wrap dynamic imports so that stale-chunk errors (after a deploy) trigger
 * a one-time full page reload instead of crashing with a MIME-type error.
 */
function lazyWithReload(factory: Parameters<typeof lazy>[0]) {
  return lazy(() =>
    factory().catch((err) => {
      const key = 'chunk-reload';
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1');
        window.location.reload();
      }
      throw err;
    }),
  );
}

// Lazy load pages for better code splitting
// OTPVerificationPage is now integrated into AuthDialog as otp-verification mode
const ProductPage = lazyWithReload(() => import('./pages/ProductPage/ProductPage'));
const AppLauncherPage = lazyWithReload(() => import('./pages/AppLauncherPage/AppLauncherPage'));
const ProfilePage = lazyWithReload(() => import('./pages/ProfilePage/ProfilePage'));
const OrderHistoryPage = lazyWithReload(() => import('./pages/OrderHistoryPage/OrderHistoryPage'));
const OrderDetailPage = lazyWithReload(() => import('./pages/OrderDetailPage/OrderDetailPage'));
const AboutUsPage = lazyWithReload(() => import('./pages/AboutUsPage/AboutUsPage'));
const PWAInstallPage = lazyWithReload(() => import('./pages/PWAInstallPage/PWAInstallPage'));
const TermsOfServicePage = lazyWithReload(() => import('./pages/TermsOfServicePage'));
const PrivacyPolicyPage = lazyWithReload(() => import('./pages/PrivacyPolicyPage'));
const CompliancePolicyPage = lazyWithReload(() => import('./pages/CompliancePolicyPage'));
const AccountDeletionPage = lazyWithReload(() => import('./pages/AccountDeletionPage'));
const SharePage = lazyWithReload(() => import('./pages/SharePage/SharePage'));
const SupportPage = lazyWithReload(() => import('./pages/SupportPage/SupportPage'));
const BlogPage = lazyWithReload(() => import('./pages/BlogPage/BlogPage'));
const BlogPostPage = lazyWithReload(() => import('./pages/BlogPostPage/BlogPostPage'));
const ProductHubRoute = lazyWithReload(() => import('./pages/ProductHubPage/ProductHubRoute'));
const RatesPage = lazyWithReload(() => import('./pages/RatesPage/RatesPage'));
const MerchantRegistrationPage = lazyWithReload(
  () => import('./pages/MerchantRegistrationPage/MerchantRegistrationPage'),
);
const FamilyPlanPage = lazyWithReload(() => import('./pages/FamilyPlanPage/FamilyPlanPage'));
const FamilyPlanDetailPage = lazyWithReload(
  () => import('./pages/FamilyPlanDetailPage/FamilyPlanDetailPage'),
);
const PhoneVerificationRedirect = lazyWithReload(
  () => import('./components/PhoneVerificationRedirect/PhoneVerificationRedirect'),
);
const EmailVerificationRedirect = lazyWithReload(
  () => import('./components/EmailVerificationRedirect/EmailVerificationRedirect'),
);
const AppleAuthCallback = lazyWithReload(
  () => import('./components/AppleAuthCallback/AppleAuthCallback'),
);
const CompleteProfilePage = lazyWithReload(() => import('./pages/CompleteProfilePage'));

import { PaymentProvider, usePayment } from './contexts/PaymentContext';
import { useAuthDialog } from './contexts/AuthDialogContext';
import { CallDialogProvider, useCallDialog } from './contexts/CallDialogContext';
import { CheckoutDialogProvider } from './contexts/CheckoutDialogContext';
import {
  MonthlyDetailsDialogProvider,
  useMonthlyDetailsDialog,
} from './contexts/MonthlyDetailsDialogContext';
import { useTranslation } from 'react-i18next';
import LazyOptimizedBankLogo from './components/OptimizedBankLogo/LazyOptimizedBankLogo';
import OpenInNewTabDialog from './components/OpenInNewTabDialog/OpenInNewTabDialog';
import { useState, useEffect, useCallback, useRef } from 'react';

import MMQRImg from './assets/mmqr.svg';
import { tokenStorage } from './utils/tokenStorage';
import { guestSessionManager } from './utils/guestSession';
import { logger } from './utils/logger';
import { getMaintenanceStatus } from './services/maintenanceService';
import MaintenancePage from './components/Maintenance/MaintenancePage';
import { getMaintenanceOverride } from './utils/maintenanceOverride';
import { useAuth } from './hooks/useAuth';
import { useToast } from './contexts/ToastContext';
import { openPaymentLink, isMobileOrPWA } from './utils/paymentUtils';
import { openPlaceholderPopup } from './utils/popupWindow';
import { appLinks } from './config/appLinks';
import {
  buildGlobalSiteRedirectUrl,
  buildMyanmarSiteRedirectUrl,
  isGlobalSiteHost,
  MYANMAR_TIMEZONE,
  getUserTimezone,
} from './utils/siteHost';

const MonthlyDetailsDialog = lazyWithReload(
  () => import('./components/MonthlyDetailsDialog/MonthlyDetailsDialog'),
);
const NotificationPermission = lazyWithReload(() =>
  import('./components/NotificationPermission').then((module) => ({
    default: module.NotificationPermission,
  })),
);
const LibraryDownloadDialog = lazyWithReload(() => import('./components/LibraryDownloadDialog'));
const CallConfirmationDialog = lazyWithReload(
  () => import('./components/CallConfirmationDialog/CallConfirmationDialog'),
);
const RoleSwitchDialog = lazyWithReload(
  () => import('./components/RoleSwitchDialog/RoleSwitchDialog'),
);

function MyanmarSiteRedirectDialog({
  redirectUrl,
  onClose,
}: {
  redirectUrl: string;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[1200] flex items-end justify-center bg-slate-950/45 px-4 pb-0 backdrop-blur-sm sm:items-center sm:pb-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="myanmar-site-redirect-title"
        className="w-full max-w-[420px] rounded-t-[28px] border border-slate-200 bg-white p-6 shadow-[0_24px_80px_rgba(15,23,42,0.24)] sm:rounded-[28px]"
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-cyan-50 text-cyan-700">
          MM
        </div>
        <h2 id="myanmar-site-redirect-title" className="mt-4 text-lg font-semibold text-slate-950">
          Myanmar site ကိုသွားချင်ပါသလား?
        </h2>
        <p className="mt-2 text-sm leading-6 text-slate-600">
          သင်သည် လက်ရှိတွင် နိုင်ငံတကာသုံး Global Site သို့ ရောက်ရှိနေပါသည်။ အကယ်၍ မြန်မာပြည်တွင်း
          Payment များ (KBZPay, WaveMoney) ဖြင့် အလွယ်တကူ ဝယ်ယူလိုပါက Myanmar Site တွင်
          ဆက်လက်အသုံးပြုနိုင်ပါသည်။
        </p>
        <div className="mt-6 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            ဒီမှာပဲနေမယ်
          </button>
          <button
            type="button"
            onClick={() => window.location.assign(redirectUrl)}
            className="flex-1 rounded-xl bg-[#04BFBF] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#03aeb0]"
          >
            Myanmar site သို့
          </button>
        </div>
      </div>
    </div>
  );
}

function GlobalSiteRedirectDialog({
  redirectUrl,
  onClose,
}: {
  redirectUrl: string;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[1200] flex items-end justify-center bg-slate-950/45 px-4 pb-0 backdrop-blur-sm sm:items-center sm:pb-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="global-site-redirect-title"
        className="w-full max-w-[420px] rounded-t-[28px] border border-slate-200 bg-white p-6 shadow-[0_24px_80px_rgba(15,23,42,0.24)] sm:rounded-[28px]"
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-700">
          <HugeiconsIcon
            name="global"
            icon={GlobalFreeIcons}
            size={24}
            color="currentColor"
            strokeWidth={1.5}
          />
        </div>
        <h2 id="global-site-redirect-title" className="mt-4 text-lg font-semibold text-slate-950">
          Global site ကိုသွားချင်ပါသလား?
        </h2>
        <p className="mt-2 text-sm leading-6 text-slate-600">
          သင်သည် လက်ရှိတွင် မြန်မာနိုင်ငံသုံး Myanmar Site သို့ ရောက်ရှိနေပါသည်။ အကယ်၍
          နိုင်ငံတကာသုံး Payment များ (Visa, MasterCard) ဖြင့် အလွယ်တကူ ဝယ်ယူလိုပါက Global Site တွင်
          ဆက်လက်အသုံးပြုနိုင်ပါသည်။{' '}
        </p>
        <div className="mt-6 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            ဒီမှာပဲနေမယ်
          </button>
          <button
            type="button"
            onClick={() => window.location.assign(redirectUrl)}
            className="flex-1 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
          >
            Global site သို့
          </button>
        </div>
      </div>
    </div>
  );
}

// Type definition for QRCodeStyling library
interface QRCodeStylingModule {
  default: new (options: QRCodeStylingOptions) => QRCodeStylingInstance;
}

interface QRCodeStylingOptions {
  width?: number;
  height?: number;
  type?: string;
  data?: string;
  image?: string;
  qrOptions?: {
    typeNumber?: number;
    mode?: string;
    errorCorrectionLevel?: string;
  };
  dotsOptions?: {
    color?: string;
    type?: string;
  };
  backgroundOptions?: {
    color?: string;
  };
  imageOptions?: {
    crossOrigin?: string;
    margin?: number;
    imageSize?: number;
  };
  cornersSquareOptions?: {
    color?: string;
    type?: string;
  };
  cornersDotOptions?: {
    color?: string;
    type?: string;
  };
}

interface QRCodeStylingInstance {
  append: (element: HTMLElement) => void;
  update: (options: Partial<QRCodeStylingOptions>) => void;
}

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  amount: number;
  onPaymentSuccess: () => void;
  paymentQrCode?: string;
  orderId?: string;
  onError?: (error: Error | unknown) => void;
  showAPlusWaitScreen?: boolean;
  aPlusPaymentData?: {
    pushNotiLink?: string;
    deepLink?: string;
    orderId?: string;
  } | null;
  onCancelAPlusPayment?: () => void;
  showCancelConfirm?: boolean;
  setShowCancelConfirm?: (show: boolean) => void;
}

function PaymentModal({
  isOpen,
  onClose,
  amount,
  onPaymentSuccess,
  paymentQrCode,
  orderId,
  showAPlusWaitScreen,
  aPlusPaymentData,
  onCancelAPlusPayment,
  showCancelConfirm,
  setShowCancelConfirm,
}: PaymentModalProps) {
  const isSafari =
    /^((?!chrome|android).)*safari/i.test(navigator.userAgent) ||
    (/Macintosh/i.test(navigator.userAgent) && /Safari/i.test(navigator.userAgent));
  const { t } = useTranslation();
  const { timeLeft, setPaymentData, clearPaymentData, globalPaymentModal, setGlobalPaymentModal } =
    usePayment();
  const [showSuccess, setShowSuccess] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [localShowCancelConfirm, setLocalShowCancelConfirm] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showOpenInNewTabConfirm, setShowOpenInNewTabConfirm] = useState(false);
  const [showAPlusCancelConfirm, setShowAPlusCancelConfirm] = useState(false);
  const qrRef = useRef<HTMLDivElement>(null);
  const qrCode = useRef<QRCodeStylingInstance | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const expirationTimerRef = useRef<NodeJS.Timeout | null>(null);
  const qrGeneratedTimeRef = useRef<number | null>(null);

  // Use props if provided, otherwise use local state
  const currentShowCancelConfirm = showCancelConfirm ?? localShowCancelConfirm;
  const currentSetShowCancelConfirm = setShowCancelConfirm ?? setLocalShowCancelConfirm;

  // Format time helper
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleGoToOrderDetail = () => {
    setShowSuccess(false);
    onClose();
    onPaymentSuccess();
  };

  const loadOrderActions = async () => import('./services/orderService');

  // Handle cancel order for MMQR
  const handleCancelOrder = async () => {
    if (!orderId || isCancelling) return;

    setIsCancelling(true);
    try {
      const { cancelOrder } = await loadOrderActions();
      await cancelOrder(orderId);

      // Clear all timers
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (expirationTimerRef.current) {
        clearTimeout(expirationTimerRef.current);
        expirationTimerRef.current = null;
      }

      currentSetShowCancelConfirm(false);
      onClose();
    } catch (error) {
      logger.error('Error cancelling order:', error);
    } finally {
      setIsCancelling(false);
    }
  };

  const handleCancelClick = () => {
    if (showAPlusWaitScreen) {
      // For A+ payment, use the existing cancel handler
      onCancelAPlusPayment?.();
    } else {
      // For MMQR, show confirmation dialog
      currentSetShowCancelConfirm(true);
    }
  };

  const handleCancelConfirm = () => {
    handleCancelOrder();
  };

  const handleCancelCancel = () => {
    currentSetShowCancelConfirm(false);
  };

  // A+ payment cancel confirmation handlers
  const handleAPlusCancelConfirm = async () => {
    setShowAPlusCancelConfirm(false);
    try {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      const id = aPlusPaymentData?.orderId ?? orderId;
      if (id) {
        const { cancelOrder } = await loadOrderActions();
        await cancelOrder(id);
      }
    } catch (error) {
      logger.error('Failed to cancel A+ payment:', error);
    }
    onCancelAPlusPayment?.();
    onClose();
  };

  const handleAPlusCancelCancel = () => {
    setShowAPlusCancelConfirm(false);
  };

  // Payment status polling function
  const checkPaymentStatus = useCallback(async () => {
    if (!orderId || isExpired) return;

    try {
      const token = tokenStorage.getAccessToken();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept-Language': 'en',
      };

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      } else {
        // Send guest ID explicitly so localhost/dev cross-site requests keep the same guest session.
        const guestId = await guestSessionManager.getSession();
        Object.assign(headers, guestSessionManager.guestIdHeaders(guestId));
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}${import.meta.env.VITE_API_V1_ORDERS_PAYMENT_STATUS.replace('{orderId}', orderId)}`,
        {
          method: 'GET',
          headers,
          credentials: 'include',
          cache: 'no-store',
        },
      );

      if (response.ok) {
        const data = await response.json();
        const paymentStatus = data.data?.paymentStatus;

        if (paymentStatus === 'paid') {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          window.location.href = `${window.location.origin}/orders/${orderId}?status=success`;
        } else if (paymentStatus === 'failed') {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          window.location.href = `${window.location.origin}/orders/${orderId}?status=failed`;
        }
      }
    } catch (error) {
      logger.error('Error checking payment status:', error);
    }
  }, [orderId, isExpired]);

  // Start polling when QR code is shown or A+ Wallet payment is initiated
  useEffect(() => {
    if (isOpen && orderId && !isExpired) {
      // For A+ Wallet, start polling immediately when wait screen is shown
      // For MMQR, start polling when QR code is available
      if (showAPlusWaitScreen || paymentQrCode) {
        pollingIntervalRef.current = setInterval(checkPaymentStatus, 3000);

        // Set up expiration timer only for MMQR (5 minutes)
        if (!showAPlusWaitScreen && paymentQrCode) {
          expirationTimerRef.current = setTimeout(
            () => {
              setIsExpired(true);
              if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
              }
              // Countdown timer cleanup is now handled by PaymentContext
            },
            5 * 60 * 1000,
          );
        }

        // Countdown timer is now handled by PaymentContext
      }
    }

    return () => {
      // Clear timers when component unmounts or effect re-runs
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (expirationTimerRef.current) {
        clearTimeout(expirationTimerRef.current);
        expirationTimerRef.current = null;
      }
      // Only clear payment data when dialog is actually closing (not when effect re-runs)
    };
  }, [
    isOpen,
    paymentQrCode,
    orderId,
    checkPaymentStatus,
    isExpired,
    setPaymentData,
    showAPlusWaitScreen,
  ]);

  // Handle cleanup when dialog closes
  const prevIsOpenRef = useRef(isOpen);
  useEffect(() => {
    // Only clear when dialog transitions from open to closed
    if (prevIsOpenRef.current && !isOpen) {
      qrGeneratedTimeRef.current = null;
      setIsExpired(false);
      clearPaymentData(); // Clear payment data when dialog closes
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, clearPaymentData]);

  useEffect(() => {
    if (isOpen && paymentQrCode) {
      if (!qrGeneratedTimeRef.current) {
        qrGeneratedTimeRef.current = Date.now();
      }

      // Set payment data in global context (timer is automatically managed)
      setPaymentData({
        amount,
        qrCode: paymentQrCode,
        type: 'topup',
      });

      // Set global payment modal
      setGlobalPaymentModal({
        ...globalPaymentModal,
        isOpen: true,
        amount,
        paymentQrCode: paymentQrCode || '',
        orderId: orderId || '',
        onPaymentSuccess,
      });

      let processedQrData;
      try {
        const utf8Bytes = new TextEncoder().encode(paymentQrCode);
        processedQrData = Array.from(utf8Bytes)
          .map((byte) => String.fromCharCode(byte))
          .join('');
      } catch (error) {
        logger.warn('Myanmar text encoding error:', error);
        processedQrData = paymentQrCode;
      }

      // Create async function to handle dynamic import with retry mechanism
      const initializeQRCode = async (retryCount = 0) => {
        try {
          const QRCodeStyling =
            (await libraryCacheManager.getQRCodeStylingLibrary()) as QRCodeStylingModule;
          qrCode.current = new QRCodeStyling.default({
            width: 250,
            height: 250,
            type: 'svg',
            data: processedQrData,
            image: MMQRImg,
            qrOptions: {
              typeNumber: 0,
              mode: 'Byte',
              errorCorrectionLevel: 'M',
            },
            dotsOptions: {
              color: '#1e40af',
              type: 'rounded',
            },
            backgroundOptions: {
              color: '#ffffff',
            },
            imageOptions: {
              crossOrigin: 'anonymous',
              margin: 2,
              imageSize: 0.2,
            },
            cornersSquareOptions: {
              color: '#1e40af',
              type: 'extra-rounded',
            },
            cornersDotOptions: {
              color: '#1e40af',
              type: 'dot',
            },
          });

          // Wait for DOM to be ready and retry if container not found
          const appendQRCode = () => {
            const qrContainer = document.getElementById('qr-code-global');
            if (qrContainer && qrCode.current) {
              qrContainer.innerHTML = '';
              qrCode.current.append(qrContainer);
              return true;
            }
            return false;
          };

          // Try to append immediately
          if (!appendQRCode()) {
            // If container not found, wait a bit and retry
            setTimeout(
              () => {
                if (!appendQRCode() && retryCount < 3) {
                  logger.warn(`Global QR container not found, retrying... (${retryCount + 1}/3)`);
                  initializeQRCode(retryCount + 1);
                } else if (retryCount >= 3) {
                  logger.error('Failed to find global QR container after 3 retries');
                }
              },
              100 * (retryCount + 1),
            ); // Exponential backoff
          }
        } catch (error) {
          logger.error('Failed to load QRCodeStyling:', error);
          // Retry on error if retryCount < 2
          if (retryCount < 2) {
            setTimeout(
              () => {
                logger.warn(`Retrying global QR code generation... (${retryCount + 1}/2)`);
                initializeQRCode(retryCount + 1);
              },
              500 * (retryCount + 1),
            );
          }
        }
      };

      // Call the async function
      initializeQRCode();
    }
  }, [
    isOpen,
    paymentQrCode,
    amount,
    globalPaymentModal,
    onPaymentSuccess,
    orderId,
    setGlobalPaymentModal,
    setPaymentData,
  ]);

  // Don't show the main dialog if it's closed
  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white/95 backdrop-blur-md border border-white/20 shadow-2xl rounded-2xl p-4 sm:p-6 w-full max-w-md mx-4">
        {showSuccess ? (
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h3 className="text-xl font-semibold mb-2">{t('payment_successful')}</h3>
            <p className="text-gray-600 mb-6">{t('your_payment_is_successful')}</p>
            <button
              onClick={handleGoToOrderDetail}
              className="w-full bg-gray-600 text-white py-3 rounded-xl font-medium hover:bg-gray-700 transition-colors"
            >
              {t('go_to_order_detail')}
            </button>
          </div>
        ) : isExpired ? (
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-semibold mb-2 text-red-600">{t('qr_code_expired')}</h3>
            <p className="text-gray-600 mb-6">{t('payment_time_expired')}</p>
            <div className="flex space-x-3">
              <button
                onClick={async () => {
                  if (!orderId) return;
                  try {
                    const { regenerateQR } = await loadOrderActions();
                    const newQrCode = await regenerateQR(orderId);
                    setIsExpired(false);
                    // Reset payment data with new QR code
                    setPaymentData({
                      amount,
                      qrCode: newQrCode,
                      type: 'topup',
                    });
                    // Update global payment modal
                    setGlobalPaymentModal({
                      ...globalPaymentModal,
                      paymentQrCode: newQrCode,
                    });
                  } catch (error) {
                    console.error('Failed to regenerate QR:', error);
                  }
                }}
                className="flex-1 bg-blue-600 text-white py-3 rounded-xl font-medium hover:bg-blue-700 transition-colors"
              >
                {t('pay_again')}
              </button>
              <button
                onClick={async () => {
                  if (!orderId) return;
                  try {
                    const { cancelOrder } = await loadOrderActions();
                    await cancelOrder(orderId);
                    onClose();
                  } catch (error) {
                    console.error('Failed to cancel order:', error);
                  }
                }}
                className="flex-1 bg-gray-600 text-white py-3 rounded-xl font-medium hover:bg-gray-700 transition-colors"
              >
                {t('cancel')}
              </button>
            </div>
          </div>
        ) : showAPlusWaitScreen ? (
          <div className="text-center">
            {/* A+ Wallet Payment Wait Screen */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{t('a_plus_payment')}</h3>
            </div>

            {/* Loading Animation */}
            <div className="mb-6">
              <div className="w-20 h-20 bg-[#04BFBF]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#04BFBF] border-t-transparent"></div>
              </div>
              <h3 className="text-xl font-semibold mb-2">{t('processing_payment')}</h3>
              <p className="text-gray-600">
                {isMobile
                  ? t('pay_with_aplus_description_mobile')
                  : t('pay_with_aplus_description')}
              </p>
            </div>

            {/* Order Details */}
            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-600">{t('amount')}</span>
                <span className="font-semibold">
                  {amount.toLocaleString()} {t('mmk')}
                </span>
              </div>
              {aPlusPaymentData?.orderId && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">{t('order_id')}</span>
                  <span className="font-mono text-sm">{aPlusPaymentData.orderId}</span>
                </div>
              )}
            </div>

            {/* Manual open A+ Wallet button */}
            <button
              onClick={() => {
                const targetUrl = isMobileOrPWA
                  ? aPlusPaymentData?.deepLink
                  : aPlusPaymentData?.pushNotiLink;
                if (!targetUrl) return;

                // Use utility to handle deep linking vs new tab
                openPaymentLink(targetUrl, () => setShowOpenInNewTabConfirm(true));
              }}
              className="w-full bg-blue-50 text-blue-700 py-3 rounded-xl font-medium hover:bg-blue-100 transition-colors border border-blue-200 mb-3"
            >
              {isSafari ? 'Open A+ Wallet in New Tab' : 'Open A+ Wallet Again'}
            </button>

            {/* Cancel Button */}
            <button
              onClick={() => {
                setShowAPlusCancelConfirm(true);
              }}
              className="w-full bg-red-600 text-white py-3 rounded-xl font-medium hover:bg-red-700 transition-colors"
            >
              {t('cancel_payment', 'Cancel Payment')}
            </button>
          </div>
        ) : (
          <div className="text-center">
            {/* Header with minimize and close buttons */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <h3 className="text-lg font-semibold">{t('payment_qr_code')}</h3>
                <div className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full">
                  {formatTime(timeLeft)}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 p-1"
                  title={t('close')}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <div ref={qrRef} data-qr-container className="bg-gray-100 rounded-xl">
              <div className="bg-white rounded-lg p-6 mx-auto">
                <div className="text-center mb-3">
                  <h2 className="text-md font-semibold text-gray-800">
                    {t('scan_qr_code_instruction')}
                  </h2>
                  <p className="text-sm text-blue-500">
                    Before{' '}
                    {new Date(
                      (qrGeneratedTimeRef.current || Date.now()) + 5 * 60 * 1000,
                    ).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                    })}{' '}
                    -{' '}
                    {new Date(
                      (qrGeneratedTimeRef.current || Date.now()) + 5 * 60 * 1000,
                    ).toLocaleTimeString('en-GB', {
                      hour12: true,
                    })}
                  </p>
                </div>

                <div className="flex justify-center mb-6">
                  <div id="qr-code-global" className="w-60 h-60"></div>
                </div>

                <div className="text-center mb-6">
                  <span className="text-3xl font-bold text-green-500">
                    {amount.toLocaleString()}
                  </span>
                  <span className="text-lg text-gray-600 ml-1">MMK</span>
                </div>

                <div className="text-center mb-4 text-sm">
                  <p className="text-sm text-gray-700">{t('mmqr_description')}</p>
                </div>

                <div className="flex justify-center flex-wrap gap-2 mb-6">
                  <LazyOptimizedBankLogo
                    bankName="A+ Wallet"
                    alt="A+ Wallet"
                    size="small"
                    enableBunnyOptimization
                  />
                  <LazyOptimizedBankLogo
                    bankName="KBZ Pay"
                    alt="KBZ Pay"
                    size="small"
                    enableBunnyOptimization
                  />
                  <LazyOptimizedBankLogo
                    bankName="Wave Money"
                    alt="Wave Money"
                    size="small"
                    enableBunnyOptimization
                  />
                  <LazyOptimizedBankLogo
                    bankName="AYA Pay"
                    alt="AYA Pay"
                    size="small"
                    enableBunnyOptimization
                  />
                  <LazyOptimizedBankLogo
                    bankName="CB Bank"
                    alt="CB Bank"
                    size="small"
                    enableBunnyOptimization
                  />
                  <LazyOptimizedBankLogo
                    bankName="UAB Pay"
                    alt="UAB Pay"
                    size="small"
                    enableBunnyOptimization
                  />
                  <LazyOptimizedBankLogo
                    bankName="CTZ Pay"
                    alt="CTZ Pay"
                    size="small"
                    enableBunnyOptimization
                  />
                  <LazyOptimizedBankLogo
                    bankName="Yoma Bank"
                    alt="Yoma Bank"
                    size="small"
                    enableBunnyOptimization
                  />
                </div>

                {/* Cancel Button for MMQR */}
                <div className="flex justify-center">
                  <button
                    onClick={handleCancelClick}
                    className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                  >
                    {t('cancel_payment', 'Cancel Payment')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <OpenInNewTabDialog
        isOpen={showOpenInNewTabConfirm}
        onCancel={() => setShowOpenInNewTabConfirm(false)}
        onOpenNewTab={() => {
          setShowOpenInNewTabConfirm(false);
          const targetUrl = isMobile ? aPlusPaymentData?.deepLink : aPlusPaymentData?.pushNotiLink;
          if (!targetUrl) return;
          try {
            const w = openPlaceholderPopup({
              title: 'A+ Wallet',
              heading: 'Redirecting to A+ Wallet…',
              message: 'If nothing happens, please allow popups.',
            });
            if (w) {
              w.location.href = targetUrl;
            } else {
              window.location.href = targetUrl;
            }
          } catch (e) {
            logger.error('Failed to pre-open A+ tab:', e);
            window.location.href = targetUrl;
          }
        }}
        title={t('payment.open_in_new_tab_title', 'Open in new tab?')}
        description={t(
          'payment.open_in_new_tab_description',
          'Safari is strict with popups. Tap confirm to open A+ in a new tab.',
        )}
      />

      {/* Cancel Confirmation Dialog for MMQR */}
      {currentShowCancelConfirm && (
        <div className="fixed inset-0 bg-black/50 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
            <h3 className="text-lg font-semibold mb-4">
              {t('confirm_cancel_order', 'Confirm Cancel Order')}
            </h3>
            <p className="text-gray-600 mb-6">
              {t(
                'cancel_order_warning',
                'Are you sure you want to cancel this order? This action cannot be undone.',
              )}
            </p>
            <div className="flex space-x-3">
              <button
                onClick={handleCancelCancel}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {t('no', 'No')}
              </button>
              <button
                onClick={handleCancelConfirm}
                disabled={isCancelling}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
              >
                {isCancelling ? t('cancelling', 'Cancelling...') : t('yes', 'Yes')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* A+ Payment Cancel Confirmation Dialog */}
      {showAPlusCancelConfirm && (
        <div className="fixed inset-0 bg-black/50 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
            <h3 className="text-lg font-semibold mb-4">
              {t('confirm_cancel_order', 'Confirm Cancel Order')}
            </h3>
            <p className="text-gray-600 mb-6">
              {t(
                'cancel_order_warning',
                'Are you sure you want to cancel this order? This action cannot be undone.',
              )}
            </p>
            <div className="flex space-x-3">
              <button
                onClick={handleAPlusCancelCancel}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {t('no', 'No')}
              </button>
              <button
                onClick={handleAPlusCancelConfirm}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                {t('yes', 'Yes')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}

// App Content Component (inside PaymentProvider)
function AppContent() {
  const { globalPaymentModal, setGlobalPaymentModal } = usePayment();
  const { showAuthDialog, hideAuthDialog } = useAuthDialog();
  const { isCallDialogOpen, closeCallDialog } = useCallDialog();
  const { trackPageView, setUserId } = useAnalytics();
  const location = useLocation();
  const { i18n } = useTranslation();
  const { isAuthenticated, user, switchRole, isLoading } = useAuth();
  const { showError } = useToast();
  const {
    isOpen: isMonthlyOpen,
    initialYear,
    initialMonth,
    closeMonthlyDetails,
  } = useMonthlyDetailsDialog();
  const isGlobalSite = isGlobalSiteHost();
  const [myanmarRedirectUrl, setMyanmarRedirectUrl] = useState<string | null>(null);
  const [globalRedirectUrl, setGlobalRedirectUrl] = useState<string | null>(null);

  // Initialize offline sync
  const { showToast } = useToast();
  // const { t } = useTranslation(); // Already imported in AppContent

  useEffect(() => {
    const userTimezone = getUserTimezone();

    if (isGlobalSite && userTimezone === MYANMAR_TIMEZONE) {
      const redirectUrl = buildMyanmarSiteRedirectUrl();
      if (redirectUrl) {
        setMyanmarRedirectUrl(redirectUrl);
      }
      return;
    }

    if (!isGlobalSite && userTimezone && userTimezone !== MYANMAR_TIMEZONE) {
      const redirectUrl = buildGlobalSiteRedirectUrl();
      if (redirectUrl) {
        setGlobalRedirectUrl(redirectUrl);
      }
    }
  }, [isGlobalSite]);

  useEffect(() => {
    const handleOnline = async () => {
      logger.log('Network status: Online');

      // Sync scratched cards
      const offlineScratchedCards = localStorage.getItem('offline_scratched_cards');
      if (offlineScratchedCards) {
        try {
          const cardsToSync: string[] = JSON.parse(offlineScratchedCards);
          if (cardsToSync.length > 0) {
            showToast('info', i18n.t('offline.syncing') || 'Syncing offline changes...');

            const results = await Promise.allSettled(
              cardsToSync.map((cardId) =>
                import('./services/orderService').then((m) => m.scratchCard(cardId)),
              ),
            );

            const failedCards: string[] = [];
            results.forEach((result, index) => {
              if (result.status === 'rejected') {
                failedCards.push(cardsToSync[index]);
                logger.error(`Failed to sync card ${cardsToSync[index]}:`, result.reason);
              }
            });

            if (failedCards.length === 0) {
              localStorage.removeItem('offline_scratched_cards');
              showToast(
                'success',
                i18n.t('offline.syncSuccess') || 'Offline changes synced successfully',
              );
            } else {
              localStorage.setItem('offline_scratched_cards', JSON.stringify(failedCards));
              showToast(
                'warning',
                i18n.t('offline.syncPartial') || 'Some changes could not be synced',
              );
            }
          }
        } catch (error) {
          logger.error('Error syncing offline cards:', error);
        }
      }
    };

    window.addEventListener('online', handleOnline);

    // Check immediately if we just came online or if there are pending items
    if (navigator.onLine) {
      handleOnline();
    }

    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [showToast, i18n]);

  // Maintenance status state and check
  const [maintenanceState, setMaintenanceState] = useState<{
    active: boolean;
    message?: string;
    updatedAt?: string;
    checked: boolean;
  }>({
    active: false,
    checked: false,
  });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const maintenanceOverride = getMaintenanceOverride();
        if (maintenanceOverride.active) {
          setMaintenanceState({
            active: true,
            message: maintenanceOverride.message,
            checked: true,
          });
          return;
        }

        const status = await getMaintenanceStatus();
        if (!mounted) return;
        if (status) {
          const allowed = status.isAllowed === true || status.isAllow === true;
          const active = status.enabled === true && !allowed; // only block when maintenance enabled and not allowed
          setMaintenanceState({
            active,
            message: status.message,
            updatedAt: status.updatedAt,
            checked: true,
          });
        } else {
          setMaintenanceState((s) => ({ ...s, checked: true }));
        }
      } catch {
        setMaintenanceState((s) => ({ ...s, checked: true }));
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Maintenance gating handled before main render to keep hooks consistent

  // Library cache management
  const { showDownloadDialog, setShowDownloadDialog } = useLibraryCache();
  const [shouldRenderNotificationPermission, setShouldRenderNotificationPermission] =
    useState(false);

  useEffect(() => {
    let idleId: number | undefined;
    let timeoutId: number | undefined;

    const enableNotificationPermission = () => {
      setShouldRenderNotificationPermission(true);
    };

    if ('requestIdleCallback' in window) {
      idleId = window.requestIdleCallback?.(enableNotificationPermission, { timeout: 3000 });
    } else {
      timeoutId = window.setTimeout(enableNotificationPermission, 1500);
    }

    return () => {
      if (idleId !== undefined && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback?.(idleId);
      }

      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, []);

  // Global effect to update URL with language parameter on route changes
  useEffect(() => {
    const updateUrlWithLanguage = () => {
      const currentUrl = new URL(window.location.href);

      if (i18n.language === 'mm') {
        currentUrl.searchParams.set('lang', 'my');
      } else {
        currentUrl.searchParams.delete('lang');
      }

      // Only update if URL actually changed
      if (currentUrl.toString() !== window.location.href) {
        window.history.replaceState({}, '', currentUrl.toString());
      }
    };

    updateUrlWithLanguage();
  }, [location.pathname, i18n.language]);

  // Merchant PG flow: prompt login or role switch when ?pg=merchant is present
  const [showRoleSwitchPrompt, setShowRoleSwitchPrompt] = useState(false);
  const [isAutoSwitchingMerchant, setIsAutoSwitchingMerchant] = useState(false);

  const cleanupPgParam = useCallback((redirectToHome = false) => {
    const currentUrl = new URL(window.location.href);
    if (currentUrl.searchParams.has('pg')) {
      currentUrl.searchParams.delete('pg');
      currentUrl.searchParams.delete('merchantSwitch');

      if (redirectToHome) {
        window.location.replace(appLinks.ezTopupHomePath);
        return;
      }

      const nextUrl = `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;
      window.history.replaceState({}, '', nextUrl);
    }
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const pageParam = urlParams.get('pg');
    const shouldAutoSwitch = urlParams.get('merchantSwitch') === 'auto';

    if (pageParam !== 'merchant') return;

    // Wait until auth state is ready to avoid showing login for authenticated users
    if (isLoading) return;

    // If already merchant, no need to prompt; just cleanup pg
    if (user?.activeRole === 'merchant') {
      cleanupPgParam(true);
      return;
    }

    if (isAuthenticated) {
      if (shouldAutoSwitch) {
        if (isAutoSwitchingMerchant) return;

        hideAuthDialog();
        setShowRoleSwitchPrompt(false);
        setIsAutoSwitchingMerchant(true);

        switchRole('merchant')
          .then(() => {
            // switchRole triggers reload on success; cleanup just in case
            cleanupPgParam(true);
          })
          .catch((err) => {
            logger.error('Failed to auto switch role to merchant', err);
            showError('Failed to switch role to merchant');
            cleanupPgParam();
          })
          .finally(() => {
            setIsAutoSwitchingMerchant(false);
          });
        return;
      }

      // Ensure any auth dialog is closed and show role switch prompt
      hideAuthDialog();
      setShowRoleSwitchPrompt(true);
    } else {
      // Prompt login first. Auto-switch links keep the query in place so this
      // effect can continue with the merchant switch after authentication.
      showAuthDialog(
        'login',
        shouldAutoSwitch
          ? undefined
          : () => {
              setShowRoleSwitchPrompt(true);
            },
      );
    }
  }, [
    isAuthenticated,
    isLoading,
    location.search,
    showAuthDialog,
    hideAuthDialog,
    user?.activeRole,
    switchRole,
    isAutoSwitchingMerchant,
    cleanupPgParam,
  ]);

  // Service Worker message listener for background notifications
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      const handleServiceWorkerMessage = (event: MessageEvent) => {
        const { type, orderId, amount } = event.data;

        if (type === 'RESTART_PAYMENT') {
          logger.log('Received restart payment message from service worker:', { orderId, amount });
          // You can implement payment restart logic here
          // For example, show a toast or modal to restart payment
          console.log(
            `Payment expired for order ${orderId} with amount ${amount}. Please restart payment.`,
          );
        }
      };

      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);

      return () => {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      };
    }
  }, []);

  // Set unique user ID for better analytics tracking
  useEffect(() => {
    const generateUserId = () => {
      const isProduction = import.meta.env.PROD;

      if (isProduction) {
        // Production: Use more sophisticated user identification
        const persistentKey = 'ga_user_id';
        const sessionKey = 'ga_session_id';

        let persistentUserId = localStorage.getItem(persistentKey);
        let sessionId = sessionStorage.getItem(sessionKey);

        if (!persistentUserId) {
          // Generate UUID-like persistent user ID
          persistentUserId =
            'user_' + Date.now().toString(36) + '_' + Math.random().toString(36).substring(2, 15);
          localStorage.setItem(persistentKey, persistentUserId);
        }

        if (!sessionId) {
          // Generate session ID for current session
          sessionId =
            'sess_' + Date.now().toString(36) + '_' + Math.random().toString(36).substring(2, 10);
          sessionStorage.setItem(sessionKey, sessionId);
        }

        // Use persistent user ID in production
        return persistentUserId;
      } else {
        // Development: Use session-based tracking for testing
        const sessionKey = 'analytics_session_user_id';
        let sessionUserId = sessionStorage.getItem(sessionKey);

        if (!sessionUserId) {
          sessionUserId =
            'dev_session_' + Math.random().toString(36).substring(2, 15) + '_' + Date.now();
          sessionStorage.setItem(sessionKey, sessionUserId);
        }

        return sessionUserId;
      }
    };

    const userId = generateUserId();
    setUserId(userId);
  }, [setUserId]);

  // Track page views on route changes
  useEffect(() => {
    trackPageView({
      page_path: location.pathname,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location, trackPageView]);

  // Check URL parameters for OTP verification and password reset functionality
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const otpFromUrl = urlParams.get('otp');
    const emailFromUrl = urlParams.get('email');
    const pageParam = urlParams.get('pg');

    if (emailFromUrl && pageParam === 'verify-email') {
      showAuthDialog('otp-verification');
      // Keep URL parameters for password reset OTP functionality
    } else if (otpFromUrl && emailFromUrl && pageParam === 'reset-password') {
      showAuthDialog('forgot-password');
      // Keep URL parameters for password reset functionality
    }
  }, [location.search, showAuthDialog]);

  // Production environment - Coming Soon page removed

  // Debug function for testing notifications
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (
        window as Window &
          typeof globalThis & {
            testNotification?: () => Promise<void>;
            requestNotificationPermission?: () => Promise<NotificationPermission | undefined>;
            checkNotificationSupport?: () => void;
          }
      ).testNotification = async () => {
        console.log('Testing notification...');
        console.log('Notification permission:', Notification.permission);

        if (Notification.permission === 'granted') {
          try {
            const notification = new Notification('Notification Enabled', {
              body: 'This is a test notification from MyEZ TopUp',
              icon: '/icon-192x192.svg',
              tag: 'test',
            });
            console.log('Notification created:', notification);

            notification.onclick = () => {
              console.log('Notification clicked');
              notification.close();
            };

            setTimeout(() => {
              notification.close();
            }, 5000);
          } catch (error) {
            console.error('Error creating notification:', error);
          }
        } else {
          console.log(
            'Notification permission not granted. Current status:',
            Notification.permission,
          );
        }
      };

      (
        window as Window &
          typeof globalThis & {
            testNotification?: () => Promise<void>;
            requestNotificationPermission?: () => Promise<NotificationPermission | undefined>;
            checkNotificationSupport?: () => void;
          }
      ).requestNotificationPermission = async () => {
        console.log('Requesting notification permission...');
        try {
          const permission = await Notification.requestPermission();
          console.log('Permission result:', permission);
          return permission;
        } catch (error) {
          console.error('Error requesting permission:', error);
        }
      };

      (
        window as Window &
          typeof globalThis & {
            testNotification?: () => Promise<void>;
            requestNotificationPermission?: () => Promise<NotificationPermission | undefined>;
            checkNotificationSupport?: () => void;
          }
      ).checkNotificationSupport = () => {
        console.log('=== Notification Support Check ===');
        console.log('Notification API supported:', 'Notification' in window);
        console.log('Service Worker supported:', 'serviceWorker' in navigator);
        console.log('Current permission:', Notification.permission);
        console.log('User agent:', navigator.userAgent);
        console.log('Protocol:', window.location.protocol);
        console.log('=== Instructions ===');
        console.log('1. To test: window.testNotification()');
        console.log('2. To request permission: window.requestNotificationPermission()');
        console.log(
          '3. To reset permission: Go to browser settings > Site settings > Notifications',
        );
        console.log('4. Make sure you are on HTTPS or localhost');
      };
    }
  }, []);

  if (maintenanceState.active) {
    return (
      <MaintenancePage message={maintenanceState.message} updatedAt={maintenanceState.updatedAt} />
    );
  }

  return (
    <>
      {/* Notification Permission Request */}
      {shouldRenderNotificationPermission && (
        <Suspense fallback={null}>
          <NotificationPermission />
        </Suspense>
      )}

      {/* Role Switch Confirmation Dialog for ?pg=merchant flow */}
      {showRoleSwitchPrompt && (
        <Suspense fallback={null}>
          <RoleSwitchDialog
            isOpen={showRoleSwitchPrompt}
            onClose={() => {
              setShowRoleSwitchPrompt(false);
              cleanupPgParam();
            }}
            onConfirm={async () => {
              try {
                await switchRole('merchant');
                // switchRole triggers reload on success; cleanup just in case
                cleanupPgParam();
              } catch (err) {
                logger.error('Failed to switch role to merchant', err);
                showError('Failed to switch role to merchant');
                setShowRoleSwitchPrompt(false);
                cleanupPgParam();
              }
            }}
          />
        </Suspense>
      )}

      <Suspense
        fallback={
          <div className="flex items-center justify-center h-dvh md:min-h-screen">
            <div className="animate-spin rounded-full h-8 w-8 border-[3px] border-[#04BFBF] border-t-transparent"></div>
          </div>
        }
      >
        <div className="h-dvh md:h-screen flex flex-col md:block overflow-hidden safe-bottom">
          <div
            data-app-scroll
            className={`flex-1 h-full overflow-y-auto overscroll-y-contain md:mb-0 ${location.pathname !== '/' && location.pathname !== '/app/install' && location.pathname !== '/about-us' && location.pathname !== '/support' && !location.pathname.startsWith('/blog') && !location.pathname.startsWith('/products/') && location.pathname !== '/account-deletion' && location.pathname !== '/compliance-policy' && !location.pathname.startsWith('/sharewith/') ? 'mb-[calc(75px+env(safe-area-inset-bottom))]' : 'mb-0'} md:pb-0 md:pt-0`}
          >
            <Routes>
              {/* Public Routes */}
              <Route
                path="/"
                element={isGlobalSite ? <Navigate to="/home" replace /> : <AppLauncherPage />}
              />
              <Route path="/home" element={<HomePage />} />

              {/* Auth routes removed - now using AuthDialog */}

              <Route
                path="/verify-phone"
                element={
                  <AuthGuard publicOnly>
                    <PhoneVerificationRedirect />
                  </AuthGuard>
                }
              />

              <Route
                path="/verify-email"
                element={
                  <AuthGuard publicOnly>
                    <EmailVerificationRedirect />
                  </AuthGuard>
                }
              />

              <Route path="/auth/apple/callback" element={<AppleAuthCallback />} />

              <Route
                path="/complete-profile"
                element={
                  <AuthGuard requireAuth>
                    <CompleteProfilePage />
                  </AuthGuard>
                }
              />

              <Route
                path="/about-us"
                element={
                  // <AuthGuard publicOnly>
                  <AboutUsPage />
                  // </AuthGuard>
                }
              />

              <Route path="/sharewith/:id" element={<SharePage />} />

              <Route
                path="/app/install"
                element={isGlobalSite ? <Navigate to="/home" replace /> : <PWAInstallPage />}
              />

              <Route path="/terms-of-service" element={<TermsOfServicePage />} />

              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />

              <Route path="/compliance-policy" element={<CompliancePolicyPage />} />

              <Route path="/account-deletion" element={<AccountDeletionPage />} />

              <Route path="/support" element={<SupportPage />} />

              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/products/:slug" element={<ProductHubRoute />} />

              <Route path="/product" element={<ProductPage />} />

              <Route path="/product/:category" element={<ProductPage />} />

              {/* <Route
        path="/checkout"
        element={
          <AuthGuard requireAuth>
            <CheckoutPage />
          </AuthGuard>
        }
      /> */}

              <Route path="/account-settings" element={<ProfilePage />} />

              <Route path="/orders" element={<OrderHistoryPage />} />

              <Route path="/orders/:orderId" element={<OrderDetailPage />} />

              {/* Merchant Registration - Protected Route */}
              <Route
                path="/merchant-registration"
                element={
                  <AuthGuard requireAuth>
                    <MerchantRegistrationPage />
                  </AuthGuard>
                }
              />
              <Route
                path="/rates"
                element={
                  <AuthGuard>
                    <RatesPage />
                  </AuthGuard>
                }
              />
              <Route path="/commission" element={<Navigate to="/rates" replace />} />

              {/* Family Plan */}
              <Route
                path="/family-plan"
                element={
                  <UserRoleGuard>
                    <FamilyPlanPage />
                  </UserRoleGuard>
                }
              />
              <Route
                path="/family-plan/:planId"
                element={
                  <UserRoleGuard>
                    <FamilyPlanDetailPage />
                  </UserRoleGuard>
                }
              />

              {/* Protected Routes */}
              {/* <Route
        path="/dashboard"
        element={
          <AuthGuard requireAuth>
            <DashboardPage />
          </AuthGuard>
        }
      /> */}

              {/* Catch-all route for 404 */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </div>
        </div>
      </Suspense>

      <PaymentModal
        isOpen={globalPaymentModal.isOpen}
        onClose={() =>
          setGlobalPaymentModal({
            ...globalPaymentModal,
            isOpen: false,
            onPaymentSuccess: globalPaymentModal.onPaymentSuccess || (() => {}),
          })
        }
        amount={globalPaymentModal.amount}
        orderId={globalPaymentModal.orderId || ''}
        paymentQrCode={globalPaymentModal.paymentQrCode || ''}
        onPaymentSuccess={globalPaymentModal.onPaymentSuccess || (() => {})}
        onError={(error: Error | unknown) => logger.error('Payment error:', error)}
        showAPlusWaitScreen={globalPaymentModal.showAPlusWaitScreen}
        aPlusPaymentData={globalPaymentModal.aPlusPaymentData}
        onCancelAPlusPayment={globalPaymentModal.onCancelAPlusPayment}
      />

      {/* Cookie Consent */}
      <CookieConsent />

      {myanmarRedirectUrl && (
        <MyanmarSiteRedirectDialog
          redirectUrl={myanmarRedirectUrl}
          onClose={() => setMyanmarRedirectUrl(null)}
        />
      )}

      {globalRedirectUrl && (
        <GlobalSiteRedirectDialog
          redirectUrl={globalRedirectUrl}
          onClose={() => setGlobalRedirectUrl(null)}
        />
      )}

      {/* Call Confirmation Dialog */}
      {isCallDialogOpen && (
        <Suspense fallback={null}>
          <CallConfirmationDialog
            isOpen={isCallDialogOpen}
            onClose={closeCallDialog}
            onConfirm={() => {
              window.location.href = 'tel:+959966854954';
              closeCallDialog();
            }}
          />
        </Suspense>
      )}

      {/* Library Download Dialog */}
      {showDownloadDialog && (
        <Suspense fallback={null}>
          <LibraryDownloadDialog
            isOpen={showDownloadDialog}
            onClose={() => setShowDownloadDialog(false)}
            onComplete={(success: boolean) => {
              if (success) {
                logger.log('Library cache updated successfully');
              }
              setShowDownloadDialog(false);
            }}
            autoStart={true}
          />
        </Suspense>
      )}

      {/* Monthly Details Dialog (Global) */}
      {isMonthlyOpen && (
        <Suspense fallback={null}>
          <MonthlyDetailsDialog
            isOpen={isMonthlyOpen}
            onClose={closeMonthlyDetails}
            initialYear={initialYear}
            initialMonth={initialMonth}
          />
        </Suspense>
      )}

      {/* Bottom Navigation for Mobile PWA */}
      {location.pathname !== '/' &&
        location.pathname !== '/app/install' &&
        location.pathname !== '/about-us' &&
        location.pathname !== '/support' &&
        location.pathname !== '/blog' &&
        !location.pathname.startsWith('/blog/') &&
        !location.pathname.startsWith('/products/') &&
        location.pathname !== '/account-deletion' &&
        location.pathname !== '/compliance-policy' &&
        !location.pathname.startsWith('/sharewith/') && <BottomNavigation />}
    </>
  );
}

function App() {
  // Global error handler for the error boundary
  const handleGlobalError = (error: Error, errorInfo: React.ErrorInfo) => {
    logger.error('🚨 Global Error Boundary caught an error:', error);
    logger.error('Error Info:', errorInfo);

    // Log to external service in production
    if (process.env.NODE_ENV === 'production') {
      try {
        // You can integrate with error tracking services here
        // Example: Sentry, LogRocket, Bugsnag, etc.
        // window.errorTracker?.captureException(error, { extra: errorInfo });

        // For now, we'll just log to console
        logger.info('📊 Error logged for production monitoring');
      } catch (loggingError) {
        logger.error('Failed to log error to external service:', loggingError);
      }
    }
  };

  return (
    <GlobalErrorBoundary onError={handleGlobalError}>
      <PaymentProvider>
        <CallDialogProvider>
          <CheckoutDialogProvider>
            <MonthlyDetailsDialogProvider>
              <AppContent />
            </MonthlyDetailsDialogProvider>
          </CheckoutDialogProvider>
        </CallDialogProvider>
      </PaymentProvider>
    </GlobalErrorBoundary>
  );
}

export default App;
